/**
 * Sandbox and Infrastructure Enums
 *
 * Enums for sandbox management and circuit breaker patterns.
 * Issue #313 - Centralize enums to shared-types
 */

import { z } from 'zod';

// ============================================================================
// SANDBOX STATUS
// ============================================================================

/**
 * Sandbox lifecycle status
 * Used by: API Gateway sandbox management, Control Tower monitoring
 */
export enum SandboxStatus {
  /** Initial state - sandbox is being provisioned */
  BOOTSTRAPPING = 'BOOTSTRAPPING',
  /** Sandbox is ready for use */
  READY = 'READY',
  /** Sandbox is actively being used */
  RUNNING = 'RUNNING',
  /** Sandbox is temporarily paused */
  PAUSED = 'PAUSED',
  /** Sandbox is experiencing issues */
  DEGRADED = 'DEGRADED',
  /** Sandbox is being cleaned up */
  CLEANUP = 'CLEANUP',
  /** Sandbox has been deleted */
  DELETED = 'DELETED',
  /** Sandbox provisioning or operation failed */
  FAILED = 'FAILED',
}

/** Zod schema for SandboxStatus validation */
export const SandboxStatusSchema = z.nativeEnum(SandboxStatus);

/** Array of all sandbox status values for iteration */
export const SANDBOX_STATUSES = Object.values(SandboxStatus);

/** Sandbox statuses that indicate the sandbox is active */
export const ACTIVE_SANDBOX_STATUSES = [
  SandboxStatus.BOOTSTRAPPING,
  SandboxStatus.READY,
  SandboxStatus.RUNNING,
  SandboxStatus.PAUSED,
  SandboxStatus.DEGRADED,
] as const;

/** Sandbox statuses that indicate the sandbox is terminated */
export const TERMINAL_SANDBOX_STATUSES = [SandboxStatus.CLEANUP, SandboxStatus.DELETED, SandboxStatus.FAILED] as const;

// ============================================================================
// CIRCUIT BREAKER STATE
// ============================================================================

/**
 * Circuit Breaker States for resilience patterns
 * Used by: API Gateway health management, gRPC client connections
 */
export enum CircuitState {
  /** Normal operation - requests pass through */
  CLOSED = 'CLOSED',
  /** Service failing - requests rejected immediately */
  OPEN = 'OPEN',
  /** Testing if service recovered - limited requests allowed */
  HALF_OPEN = 'HALF_OPEN',
}

/** Zod schema for CircuitState validation */
export const CircuitStateSchema = z.nativeEnum(CircuitState);

/** Array of all circuit states for iteration */
export const CIRCUIT_STATES = Object.values(CircuitState);
